import { useState, type FormEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { FiestaBrandLogo } from '../components/FiestaBrandLogo'
import { authForgotPassword } from '../api/auth'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const [emailErr, setEmailErr] = useState<string | undefined>()

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setOkMsg(null)
    if (!email.trim()) {
      setEmailErr('Email é obrigatório')
      return
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailErr('Email inválido')
      return
    }
    setEmailErr(undefined)
    setLoading(true)
    try {
      const msg = await authForgotPassword(email.trim())
      setOkMsg(msg)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao pedir redefinição')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
          bgcolor: 'background.default',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <FiestaBrandLogo height={56} sx={{ mb: 2, maxWidth: 280 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Esqueci a senha
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Indique o e-mail da conta. Se existir e estiver activa, enviamos um link para
            redefinir a senha.
          </Typography>

          {error ? (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          {okMsg ? (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {okMsg}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={onSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailErr) setEmailErr(undefined)
              }}
              error={Boolean(emailErr)}
              helperText={emailErr}
              disabled={loading || Boolean(okMsg)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2, py: 1.5 }}
              disabled={loading || Boolean(okMsg)}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Enviar link'}
            </Button>
          </Box>

          <Link component={RouterLink} to="/login" underline="hover" variant="body2">
            Voltar ao login
          </Link>
        </Paper>
      </Box>
    </Container>
  )
}
